import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Zap, Truck, RotateCcw, Check, Minus, Plus, ChevronRight, ZoomIn, ShieldCheck, Ruler, BedDouble,
} from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { StarRating } from "@/components/StarRating";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { formatPrice } from "@/lib/utils";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => {
      setData(r.data);
      setSize(r.data.product.sizes[0]);
      setActiveImg(0);
      setQty(1);
    }).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="mx-auto max-w-7xl px-6 py-24"><div className="grid gap-12 lg:grid-cols-2"><div className="aspect-square animate-pulse rounded-3xl bg-secondary" /><div className="space-y-4"><div className="h-8 w-2/3 animate-pulse rounded bg-secondary" /><div className="h-6 w-1/3 animate-pulse rounded bg-secondary" /></div></div></div>;
  if (!data) return <div className="flex flex-col items-center justify-center py-32"><p className="font-serif text-3xl">Produit introuvable</p><Link to="/canapes" className="mt-6 rounded-full bg-obsidian px-6 py-3 text-sm font-semibold text-cream">Retour à la boutique</Link></div>;

  const { product, similar } = data;
  const lifestyleImg = product.gallery[product.gallery.length - 1] || product.image;

  const addToCart = () => addItem(product, { size, quantity: qty });
  const buyNow = () => { addItem(product, { size, quantity: qty }); navigate("/commande"); };

  return (
    <div className="pt-6">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <nav className="mb-8 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="breadcrumb">
          <Link to="/" className="hover:text-gold-dark">Accueil</Link><ChevronRight size={12} />
          <Link to={`/canapes?category=${product.categoryId}`} className="hover:text-gold-dark">{product.category}</Link><ChevronRight size={12} />
          <span className="text-foreground">{product.name}</span>
        </nav>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Gallery */}
          <div>
            <motion.div
              className="group relative cursor-zoom-in overflow-hidden rounded-[1.8rem] shadow-luxe"
              onClick={() => setZoom(true)}
              data-testid="product-main-image"
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeImg}
                  src={product.gallery[activeImg]}
                  alt={product.name}
                  initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="aspect-square w-full object-cover"
                />
              </AnimatePresence>
              <span className="absolute right-4 top-4 rounded-full bg-cream/90 p-2.5 text-obsidian opacity-0 transition-opacity group-hover:opacity-100"><ZoomIn size={16} /></span>
            </motion.div>
            {product.gallery.length > 1 && (
              <div className="mt-4 flex gap-3">
                {product.gallery.map((g, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
                    className={`overflow-hidden rounded-xl transition-all ${activeImg === i ? "ring-2 ring-gold ring-offset-2" : "opacity-60 hover:opacity-100"}`}>
                    <img src={g} alt="" className="h-20 w-20 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {product.badge && (
              <span className="mb-3 inline-block rounded-full bg-gold-gradient px-3 py-1 text-[10px] font-accent font-semibold uppercase tracking-widest text-obsidian">{product.badge}</span>
            )}
            <h1 className="font-serif text-3xl font-medium lg:text-5xl" data-testid="product-name">{product.name}</h1>
            <div className="mt-3 flex items-center gap-3">
              <StarRating rating={product.rating} size={16} />
              <span className="text-sm text-muted-foreground">{product.rating} · {product.reviewsCount} avis</span>
            </div>

            <div className="mt-6 flex items-end gap-3">
              <span className="text-3xl font-semibold text-foreground" data-testid="product-price">{formatPrice(product.price)}</span>
              {product.oldPrice && <span className="mb-1 text-lg text-muted-foreground line-through">{formatPrice(product.oldPrice)}</span>}
              {product.oldPrice && <span className="mb-1 rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive">-{Math.round((1 - product.price / product.oldPrice) * 100)}%</span>}
            </div>

            <p className="mt-6 text-muted-foreground">{product.description}</p>

            {(product.dimensions || product.sleeping) && (
              <div className="mt-5 flex flex-wrap gap-3" data-testid="product-dimensions">
                {product.dimensions && (
                  <span className="flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-xs font-medium text-foreground"><Ruler size={14} className="text-gold-dark" /> {product.dimensions}</span>
                )}
                {product.sleeping && (
                  <span className="flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-xs font-medium text-foreground"><BedDouble size={14} className="text-gold-dark" /> Couchage {product.sleeping}</span>
                )}
              </div>
            )}

            {/* Sizes */}
            <div className="mt-8">
              <p className="mb-3 text-sm font-semibold">Configuration</p>
              <div className="flex flex-wrap gap-2.5">
                {product.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-option-${s.replace(/\s/g, "-")}`}
                    className={`rounded-full border px-5 py-2.5 text-sm transition-colors ${size === s ? "border-gold bg-gold text-obsidian" : "border-border hover:border-gold"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity + actions */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-4 rounded-full border border-border px-4 py-3">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease"><Minus size={16} /></button>
                <span className="w-6 text-center font-medium" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} data-testid="qty-increase"><Plus size={16} /></button>
              </div>
              <span className={`flex items-center gap-1.5 text-sm ${product.inStock ? "text-emerald-700" : "text-destructive"}`}>
                <span className={`h-2 w-2 rounded-full ${product.inStock ? "bg-emerald-500" : "bg-destructive"}`} />
                {product.inStock ? "En stock" : "Épuisé"}
              </span>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button onClick={addToCart} data-testid="add-to-cart-button"
                className="group flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-obsidian py-4 text-sm font-semibold text-obsidian transition-colors hover:bg-obsidian hover:text-cream">
                <ShoppingBag size={17} /> Ajouter au panier
              </button>
              <button onClick={buyNow} data-testid="buy-now-button"
                className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-gold-gradient py-4 text-sm font-semibold text-obsidian transition-transform hover:scale-[1.02]">
                <Zap size={17} /> Acheter maintenant
              </button>
            </div>

            {/* Delivery / returns */}
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {[[Truck, "Livraison offerte", product.delivery], [RotateCcw, "Retours 30 jours", product.returns], [ShieldCheck, "Garantie 10 ans", "Structures et assemblages garantis."]].map(([Icon, t, d], i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-4">
                  <Icon size={18} className="mb-2 text-gold-dark" />
                  <p className="text-xs font-semibold">{t}</p>
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground line-clamp-3">{d}</p>
                </div>
              ))}
            </div>

            {/* Characteristics accordion */}
            <Accordion type="single" collapsible className="mt-8" defaultValue="features">
              <AccordionItem value="features">
                <AccordionTrigger data-testid="features-accordion" className="font-serif text-lg">Caractéristiques</AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    {product.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><Check size={15} className="mt-0.5 shrink-0 text-gold-dark" /> {f}</li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="delivery">
                <AccordionTrigger className="font-serif text-lg">Livraison & Retours</AccordionTrigger>
                <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                  <p>{product.delivery}</p><p>{product.returns}</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>

        {/* Realistic environment */}
        <Reveal className="mt-24">
          <div className="relative overflow-hidden rounded-[2rem] shadow-luxe">
            <img src={lifestyleImg} alt="Le canapé dans un intérieur réaliste" className="h-[380px] w-full object-cover lg:h-[520px]" data-testid="lifestyle-image" />
            <div className="absolute inset-0 bg-gradient-to-r from-obsidian/70 to-transparent" />
            <div className="absolute inset-y-0 left-0 flex max-w-lg flex-col justify-center p-8 lg:p-16">
              <p className="mb-2 font-accent text-xs uppercase tracking-[0.25em] text-gold">Mise en situation</p>
              <h2 className="font-serif text-3xl text-white lg:text-4xl">Imaginez-le chez vous</h2>
              <p className="mt-3 text-white/70">Le {product.name} s'intègre naturellement dans les intérieurs contemporains comme classiques. Une pièce maîtresse qui redéfinit votre espace de vie.</p>
            </div>
          </div>
        </Reveal>

        {/* Similar */}
        {similar?.length > 0 && (
          <section className="mt-24 pb-8">
            <Reveal className="mb-10 text-center">
              <p className="mb-2 font-accent text-xs uppercase tracking-[0.25em] text-gold-dark">Vous aimerez aussi</p>
              <h2 className="font-serif text-3xl font-medium lg:text-4xl">Produits similaires</h2>
            </Reveal>
            <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
              {similar.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </section>
        )}
      </div>

      {/* Zoom modal */}
      <AnimatePresence>
        {zoom && (
          <motion.div className="fixed inset-0 z-[110] flex items-center justify-center bg-obsidian/90 p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setZoom(false)} data-testid="zoom-modal">
            <motion.img src={product.gallery[activeImg]} alt="" className="max-h-full max-w-full rounded-2xl object-contain" initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
